import { computed, ref, watch } from 'vue'
import useToasts from './useToasts'
import { useI18n } from 'vue-i18n'
import { dataSpaceDiscoveryApi } from '@/api'
import { LookupShellResponse, FilterExpressionKeyEnum } from '@coac-gmbh/saifty-orchestrator-api'
import { CanonicalAction, DataAsset, DataObjectDTO, DataObjectsInfo } from '@coac-gmbh/saifty-main-apis'
import { SDS_CANONICAL_NAME } from '@/config'
import { OrganizationDetailDto } from '@coac-gmbh/saifty-organization-api'

export const DATA_SPACE_URL_ID = 'data-space'
export const CERTIFICATE_URL_ID = 'certificate'
export const CERTIFICATE_PUSHED_TYPE = 'pushed'
export const CANONICAL_CERTIFICATE_DOCUMENT = 'DOCUMENT.CONTENTBASE64'
export const CANONICAL_CERTIFICATE_DOCUMENT_ID = 'DOCUMENT.DOCUMENTID'
export const CANONICAL_DATA_SPACE_SHELL_NAME = 'SEC_0.DATA_SPACE_SHELL_NAME'
export const CANONICAL_DATA_SPACE_GLOBAL_REFERENCE = 'SEC_0.DATA_SPACE_GLOBAL_REFERENCE'

//CERTIFICATE CANONICALS
export const CANONICAL_CERTIFICATE_TYPE = 'DATA.CERTIFICATETYPE'
export const CANONICAL_CERTIFICATE_NUMBER = 'DATA.CERTIFICATENUMBER'

export enum CertificateRejectionType {
  PULL = 'pull',
  PUSH = 'push',
}

export interface DataSpaceLookupParams {
  bpn: string
  counterPartyAddress: string
}

export interface OrganizationDetailDtoExtended extends OrganizationDetailDto {
  isOpen: boolean
  isOwner: boolean
}

export interface DataAssetExtended extends DataAsset {
  subModels?: any[]
}

export interface DataObjectExtended extends DataObjectDTO {
  isLoading?: boolean
  subModels?: any[]
}

export interface DataObjectInfoExtended extends DataObjectsInfo {
  subModels?: any[]
}

export interface CertificateNotificationData {
  certificateStatus?: string
  requestType?: string
  header?: {
    context?: string
    senderBpn?: string
    receiverBpn?: string
    messageId?: string
    relatedMessageId?: string
    version?: string
    sentDateTime?: string
    senderFeedbackUrl?: string
  }
  id?: string
  content?: {
    certifiedBpn?: string
    certificateType?: string
    locationBpns?: string[]
  }
}

export default function useDataSpace({ loadCounterPartyAddressOptions = false } = {}) {
  const { addToast } = useToasts()
  const { t } = useI18n()
  const bpnOptions = (window.ENV.SAIFTY_DATA_SPACE_BPN_OPTIONS || '')
    ?.split(',')
    .map((s) => s.trim())
    .filter((opt) => !!opt)
  const bpnOptionsForSelector = bpnOptions.map((opt) => {
    const [bpn, companyName] = opt.split(':')
    return { name: companyName ? `${bpn} - ${companyName}` : bpn, value: bpn }
  })
  const bpnSelected = ref<string>('')
  const counterPartySelected = ref<string>('')
  const counterPartyAddressOptions = ref<string[]>([])
  const loadingCounterParty = ref(false)

  const counterPartyAddressOptionsForSelector = computed(() => {
    return counterPartyAddressOptions.value.map((opt) => ({ name: opt, value: opt }))
  })

  async function loadCounterPartyAddressByBPN() {
    await new Promise((resolve) => setTimeout(resolve, 100)) // Allow time for counterPartySelected to be reset to empty if needed
    try {
      loadingCounterParty.value = true
      const { data } = await dataSpaceDiscoveryApi.discoverConnector({ bpn: bpnSelected.value })
      counterPartyAddressOptions.value = data
      if (data.length && !counterPartySelected.value) {
        counterPartySelected.value = data[0]
      }
    } catch (error) {
      counterPartyAddressOptions.value = []
      addToast({ text: t('Error loading counter party addresses'), variant: 'danger' })
    } finally {
      loadingCounterParty.value = false
    }
  }

  function dataSpaceSearchTags() {
    return [
      {
        name: t('Customer Part ID'),
        value: FilterExpressionKeyEnum.CustomerPartId,
      },
      {
        name: t('Manufacturer Part ID'),
        value: FilterExpressionKeyEnum.ManufacturerPartId,
      },
      {
        name: t('Manufacturer ID'),
        value: FilterExpressionKeyEnum.ManufacturerId,
      },
      {
        name: t('Batch ID'),
        value: FilterExpressionKeyEnum.BatchId,
      },
      {
        name: t('Intrinsic ID'),
        value: FilterExpressionKeyEnum.IntrinsicId,
      },
      {
        name: t('Asset ID'),
        value: FilterExpressionKeyEnum.AssetId,
      },
      {
        name: t('Semantic ID'),
        value: FilterExpressionKeyEnum.SemanticId,
      },
      {
        name: t('Global Asset ID'),
        value: FilterExpressionKeyEnum.GlobalAssetId,
      },
    ]
  }

  function shellsToDataAsset(data: LookupShellResponse, dataAssetId: string): DataAssetExtended {
    const shellFound = data.shells.find((s) => s.id && s.id.toString() === dataAssetId)
    const submodels = ((shellFound?.submodelDescriptors as any[]) || []).map((obj) => ({
      id: obj.id,
      name: obj.idShort,
    }))

    return {
      id: shellFound?.id ? shellFound.id.toString() : '',
      name: shellFound?.idShort ? shellFound.idShort.toString() : '',
      data: submodels,
      total: submodels.length,
      subModels: shellFound?.submodelDescriptors as any[],
    }
  }

  function shellsToDataObjects(data: LookupShellResponse): DataObjectExtended[] {
    const shells = data.shells || []
    return shells.flatMap((shell) => {
      return ((shell?.submodelDescriptors as any[]) || []).map(
        (obj): DataObjectExtended => ({
          id: obj.id,
          dataAssetsId: shell?.id ? shell.id.toString() : '',
          data: [
            {
              fieldName: SDS_CANONICAL_NAME,
              fieldValue: obj.idShort || obj.id || '--',
              action: CanonicalAction.Verified,
              confidence: 100,
              location: [],
              props: [],
            },
            {
              fieldName: CANONICAL_DATA_SPACE_SHELL_NAME,
              fieldValue: shell?.idShort ? shell.idShort.toString() : '',
              action: CanonicalAction.Verified,
              confidence: 100,
              location: [],
              props: [],
            },
            {
              fieldName: CANONICAL_DATA_SPACE_GLOBAL_REFERENCE,
              fieldValue:
                obj.semanticId?.keys
                  ?.find((k: { type: string; value?: string }) => k.type === 'GlobalReference')
                  ?.value?.split('/')
                  ?.pop() || '',
              action: CanonicalAction.Verified,
              confidence: 100,
              location: [],
              props: [],
            },
          ],
          subModels: shell?.submodelDescriptors as any[],
        }),
      )
    })
  }

  if (loadCounterPartyAddressOptions) {
    watch(
      bpnSelected,
      () => {
        if (bpnSelected.value) {
          loadCounterPartyAddressByBPN()
        } else {
          counterPartyAddressOptions.value = []
          counterPartySelected.value = ''
        }
      },
      { immediate: false },
    )
  }

  return {
    loadingCounterParty,
    bpnSelected,
    bpnOptions,
    bpnOptionsForSelector,
    counterPartySelected,
    counterPartyAddressOptions,
    counterPartyAddressOptionsForSelector,
    loadCounterPartyAddressByBPN,
    dataSpaceSearchTags,
    shellsToDataAsset,
    shellsToDataObjects,
  }
}
